import type { Settlement } from './settlements';
import type { Registry } from '../content/registry';
import type { Road, RoadClass, ZoneName } from '../content/types';
import { PIECE } from './overlay';
import type { Junction, PathClass, PathEdge, PathNode } from './paths';
import { CELL, HALF_WIDTH, PathNet, edgeAt } from './paths';
import { hash, Rng } from './rng';
import { OVER_FLAT, OVER_H, OVER_RAMP, RAMP_GRADE, RoadNet, type Axis, type RoadHit } from './roads';
import type { Fields } from './fields';

/**
 * The **compatibility view** the fork decided on (docs/MAPGEN_DECISION.md): `hitsAt` answered from the path network
 * instead of the lattice, so the AI drivers, the chunk generator, growth, crews, parking and the static raster keep
 * asking the one question they have always asked while the network underneath them changes shape.
 *
 * The whole adapter rests on one idea. `RoadNet` models a road as *a line at a constant coordinate*: for axis `'v'`,
 * every row of tiles meets the road in the same columns, `[pos - floor((w-1)/2), pos + floor(w/2)]`. A path edge is
 * the same thing with `pos` allowed to move: for each row, the centre column is wherever the curve crosses that row.
 * So a `PathEdge` is rasterised here as a **scanline-thickened curve**, and every invariant the callers actually lean
 * on survives unchanged — `pos + off === t`, `off === 0` on the centre column, `RoadNet.covers(0, width, off)` meaning
 * "on the carriageway", `|off| <= width/2 + slack` meaning "still on the road".
 *
 * What that model costs is stated where it is paid, below. In short: a road running at 45° comes out √2 wider than
 * its nominal width (measured square to itself), and `pos` is a *local* coordinate rather than a line identity. The
 * identity moved to `k`.
 */

/** How far outside an edge's cached bounding box a hit may still land. The box is padded by the *spike's* half-width
 *  (`HALF_WIDTH`), and a content road can be wider than that, so the box alone is not a safe reject. */
const BOX_PAD = 8;

/** Hoisted so a query allocates nothing at all when it misses — most tiles in the world are not road. */
const byLevel = (a: RoadHit, b: RoadHit): number => a.level - b.level;

/** When no road of an edge's own class exists in the registry, fall back down this ladder rather than invent one. */
/** (§56) a city this big gets three blocks to a cell in its core (two cross streets); smaller cities two (one) */
const CORE_BIG = 500;
const LADDER: Record<PathClass, RoadClass[]> = {
  highway: ['highway', 'avenue', 'street'],
  avenue: ['avenue', 'street', 'highway'],
  street: ['street', 'avenue', 'lane'],
  lane: ['lane', 'street', 'dirt'],
  dirt: ['dirt', 'lane', 'street'],
  rail: ['rail'],
};

/** What the adapter works out once per edge and then never again: the things that are constant along the whole road. */
/** (§42) a point on a driving lane: position on the tile centres, unit tangent in the direction of travel, deck height */
export interface LanePoint { x: number; y: number; tx: number; ty: number; h: number }

export interface EdgeInfo {
  road: Road;
  /** total tiles the road occupies across, carriageway + both sidewalks — the same number `RoadNet.hitsAlong` uses */
  total: number;
  axis: Axis;
  /** the line identity `RoadHit.k` carries; see `edgeKey` */
  k: number;
  /** (track 2) the scanline table: the centre column for every integer row along the axis, from `u0` — built once per
   *  edge, so a tile query is one array read instead of a walk over the curve's segments (docs/MAPGEN_DECISION.md §5) */
  u0: number;
  centres: Float32Array;
  /** (track 2) the deck height per row, indexed like `centres`, or undefined for a road that stays on the ground —
   *  which is nearly every road, so the array exists only where a bridge or a raised crossing does */
  deck?: Float32Array;
}

/**
 * (track 2, the network swap) What `WorldGen` and the game read from *a* road network, whichever one runs underneath:
 * the lattice (`RoadNet`) and the path adapter (`PathRoads`) both provide it, and `WorldGen.net` is typed by it. The
 * lattice-only readers (`lineAt`, `lineDef`: the 16-tile grid of lines) stay in the interface so the traffic lights,
 * the cameras and the paper map compile; on the paths they answer "no line", and go quiet until §21's junction objects.
 */
export interface RoadSource {
  readonly fields: Fields;
  hitsAt(x: number, y: number, out?: RoadHit[]): RoadHit[];
  overpassH(h: RoadHit, x: number, y: number): number;
  frontageAt(x: number, y: number): { axis: Axis; road: Road } | null;
  generatedAt(axis: Axis, t: number, u: number, out?: RoadHit[]): boolean;
  lineAt(axis: Axis, t: number, u: number): ReturnType<RoadNet['lineAt']>;
  lineDef(axis: Axis, level: number, k: number, seg: number): Road | null | undefined;
}

export class PathRoads implements RoadSource {
  /**
   * Per-edge, keyed on the edge object rather than its string key: `PathNet` hands back the *same* cached object for
   * the same edge, and a `WeakMap` lookup costs no allocation and lets the entry die with the net's own cache when it
   * is cleared. A string key would mean building a key string on the per-tile path, which is exactly what the path
   * network went to the trouble of avoiding.
   */
  private info = new WeakMap<PathEdge, EdgeInfo>();
  /** (track 1) the junction box of each node cell, `null` where the cell has no junction or the junction is an interchange */
  private boxes = new Map<number, JunctionBox | null>();

  constructor(readonly net: PathNet, readonly reg: Registry = net.fields.reg) {}
  get fields(): Fields { return this.net.fields; }
  /** the lattice's lines do not exist here: the readers that asked for one (lights, cameras, the map's road lines) get none until the junction objects (§21) */
  lineAt(_axis: Axis, _t: number, _u: number): ReturnType<RoadNet['lineAt']> { return null; }
  lineDef(_axis: Axis, _level: number, _k: number, _seg: number): Road | null { return null; }

  /**
   * A real `Road` from the registry for an edge's class — never an invented one. Which of several roads of the same
   * class is chosen is hashed against the edge and weighted by the zone at its midpoint, the way `RoadNet.computeRaw`
   * chooses, so a downtown avenue can come out a boulevard and a rural one cannot.
   */
  private roadFor(e: PathEdge): Road {
    const mx = (e.a.x + e.b.x) / 2, my = (e.a.y + e.b.y) / 2;
    const zone: ZoneName = this.net.fields.urbanZone(mx, my);
    const rng = new Rng(hash(this.net.seed + 0x9b1, e.a.cx, e.a.cy, e.level, e.b.cx > e.a.cx ? 0 : 1));
    const all = this.reg.all('road');
    for (const cls of LADDER[e.cls]) {
      const zoned = all.filter((r) => r.class === cls && (r.zones[zone] ?? 0) > 0);
      if (zoned.length) return rng.weighted(zoned, (r) => r.zones[zone] ?? 0) ?? zoned[0];
      const any = all.filter((r) => r.class === cls);
      if (any.length) return any[0];
    }
    // Nothing of any class in the ladder exists. Rather than fabricate a Road (which would put an object in the world
    // that `reg.all('road')` does not know about, and every caller that looks a road up by id would then miss), take
    // whatever road the registry does have. An empty registry is a content bug and belongs in `content -- validate`.
    const fallback = all.find((r) => r.class !== 'rail') ?? all[0];
    if (!fallback) throw new Error('PathRoads: the registry has no roads at all');
    return fallback;
  }

  /**
   * (track 2) `RoadNet.generatedAt`'s question — is a *generated* road (not a player-built piece) under this tile — answered
   * from the paths. The lattice asked it by line (`axis, t, u`); a path has no line to name, so the answer is about the
   * tile, and the axis is honoured by asking for a hit that runs that way. growth.ts's `planCheck` is the one caller.
   */
  generatedAt(axis: Axis, t: number, u: number, out: RoadHit[] = []): boolean {
    const [x, y] = axis === 'v' ? [t, u] : [u, t];
    return this.hitsAt(Math.floor(x), Math.floor(y), out).some((h) => !h.built && h.road.class !== 'rail');
  }
  /**
   * (track 1) `growth.ts`'s `endTouches` on the paths: does the end (pos, e) of a piece on `axis` reach a generated
   * road within `ATTACH_REACH` tiles in either direction — and if so how many tiles the generator must pave past the
   * end so the piece meets the kerb. `PathNet.attachment` does the work; this is the seam growth and the generator ask.
   */
  attachment(axis: Axis, pos: number, e: number, only?: 1 | -1): { gap: number; toward: 1 | -1; road: Road } | undefined {
    let best: { gap: number; toward: 1 | -1; road: Road } | undefined;
    for (const toward of [1, -1] as const) {
      if (only && toward !== only) continue; // (planning.ts asks each end of a piece for its own side)
      const a = this.net.attachment(axis, pos, e, toward);
      if (a && (!best || a.gap < best.gap)) best = { gap: a.gap, toward, road: this.roadOf(a.edge).road };
    }
    return best;
  }
  /**
   * (track 2, the deck seam) `RoadNet.overpassH`'s question — how high is the deck under this hit — answered from the
   * hit itself: the adapter wrote the row's deck height on it, so the generator's per-hit call is a field read. The
   * lattice raised a highway over a level-0 crossing by geometry; a path edge is raised where its centre line crosses
   * water (a bridge, found here) or where the network resolved an `Over` (a crossing, track 1's). `x, y` are kept for
   * the signature's sake: the hit already is the tile.
   */
  overpassH(h: RoadHit, _x: number, _y: number): number { return h.deck ?? 0; }
  /**
   * (track 2) The lattice's slip lanes beside an overpass, `RoadNet.frontageAt`. A path network has no interchange to
   * put them at yet, and the owner's order has interchanges last (Q77) — so, honestly, none.
   */
  frontageAt(_x: number, _y: number): { axis: Axis; road: Road } | null { return null; }
  /** (track 2) where an edge's centre line crosses the integer row `u` along its axis, or undefined past its ends */
  centreAt(i: EdgeInfo, u: number): number | undefined {
    const j = u - i.u0; if (j < 0 || j >= i.centres.length) return undefined;
    const c = i.centres[j]; return Number.isNaN(c) ? undefined : c;
  }
  // ---- (§56, owner Sep 12) the pre-grown core: cross streets between the avenues inside a city's core radius ----
  //
  // A city's downtown on the main server is a grid with a shop on every corner; the path network's 64 m blocks leave
  // its inside empty. So inside a city's core the adapter lays the cross streets the city would have grown: straight
  // east–west lanes between one north–south avenue and the next, three blocks to a cell in the biggest cities
  // (Manhattan's shape at the game's scale) and two in the others. They are synthetic edges — the same object the
  // network's edges are, so the tiles, the strokes, the lots and the traffic all see them — kept here and never in
  // the network, whose cells stay 64 m. Outside a core, nothing changes.
  private coreCache = new Map<number, PathEdge[]>();
  private coreNearCache = new Map<number, PathEdge[]>();
  private coreSet = new WeakSet<PathEdge>();
  /** the two avenues a core street runs between: their painted widths, for the stroke painter's junction ends */
  private coreBox = new WeakMap<PathEdge, [number, number]>();
  /** is this edge one of the core's cross streets (not the network's own)? */
  isCore(e: PathEdge): boolean { return this.coreSet.has(e); }
  /** the city whose core holds (x, y), if any */
  private coreCity(x: number, y: number): Settlement | undefined {
    for (const s of this.net.fields.settlements.near(x, y)) if (s.size === 'city' && s.core > 0 && Math.hypot(x - s.x, y - s.y) < s.core) return s;
    return undefined;
  }
  /** the cross streets whose rows lie in cell (cx, cy): between the cell's own north–south edge and the next cell's */
  coreEdges(cx: number, cy: number): PathEdge[] {
    const key = (cx & 0x3fffff) * 0x400000 + (cy & 0x3fffff);
    let list = this.coreCache.get(key); if (list) return list;
    if (this.coreCache.size > 20000) { this.coreCache.clear(); this.coreNearCache.clear(); }
    list = [];
    const city = this.coreCity((cx + 0.5) * CELL, (cy + 0.5) * CELL);
    if (city) {
      // the cell's own north–south edge and the next one east; the mesh has gaps even downtown, so a missing avenue is
      // looked for up to two cells further out and the street spans the wider block
      const southEdge = (kx: number): PathEdge | undefined => { const p0 = this.net.node(kx, cy), p1 = this.net.node(kx, cy + 1); if (p0.wet || p1.wet) return undefined; return this.net.bucket(kx, cy).find((e) => e.a === p0 && e.b === p1 && e.cls !== 'rail'); };
      const west = southEdge(cx), east = southEdge(cx + 1) ?? southEdge(cx + 2) ?? southEdge(cx + 3);
      if (west && east) {
        const wi = this.infoFor(west), ei = this.infoFor(east);
        if (wi.axis === 'v' && ei.axis === 'v') {
          // the rows sit between the node rows both avenues share, so each row has a centre on both
          const top = Math.max(west.a.y, east.a.y), bottom = Math.min(west.b.y, east.b.y);
          const rows = city.radius >= CORE_BIG ? 3 : 2; // (A: three blocks to a cell in the biggest cities; B: two)
          for (let k = 1; k < rows; k++) {
            const y = Math.round(top + ((bottom - top) * k) / rows);
            const xw = this.centreAt(wi, y), xe = this.centreAt(ei, y);
            if (xw === undefined || xe === undefined || xe - xw < 12) continue;
            const e = this.coreEdge(cx, cy, k, Math.round(xw), Math.round(xe), y, west.a.density, west.pts.length / 2);
            this.coreBox.set(e, [wi.total, ei.total]);
            list.push(e);
          }
        }
      }
    }
    this.coreCache.set(key, list);
    return list;
  }
  private coreEdge(cx: number, cy: number, k: number, xw: number, xe: number, y: number, density: number, n: number): PathEdge {
    const a: PathNode = { x: xw, y, cx, cy, density, wet: false }, b: PathNode = { x: xe, y, cx: cx + 1, cy, density, wet: false };
    const pts = new Float64Array(n * 2);
    for (let i = 0; i < n; i++) { const t = i / (n - 1); pts[i * 2] = xw + (xe - xw) * t; pts[i * 2 + 1] = y; }
    const half = 4;
    const e: PathEdge = { a, b, cls: 'lane', level: 3, qx: (xw + xe) / 2, qy: y, pts, x0: xw - half, y0: y - half, x1: xe + half, y1: y + half, key: `core:${cx},${cy},${k}`, overs: [] };
    this.coreSet.add(e);
    return e;
  }
  /** the core streets that can reach a point: those of the 3×3 cells round it (a street's ends stray into the neighbours) */
  private coreNear(x: number, y: number): PathEdge[] {
    const cx = Math.floor(x / CELL), cy = Math.floor(y / CELL), key = (cx & 0x3fffff) * 0x400000 + (cy & 0x3fffff);
    let list = this.coreNearCache.get(key); if (list) return list;
    list = [];
    for (let dy = -1; dy <= 1; dy++) for (let dx = -3; dx <= 1; dx++) for (const e of this.coreEdges(cx + dx, cy + dy)) if (e.x1 >= cx * CELL - 2 && e.x0 <= (cx + 1) * CELL + 2) list.push(e); // (a street keyed to its west cell may span three cells east)
    this.coreNearCache.set(key, list);
    return list;
  }
  /** every edge that can reach a cell, the network's and the core's — what the lot planner walks */
  bucket(cx: number, cy: number): PathEdge[] {
    const core = this.coreNear((cx + 0.5) * CELL, (cy + 0.5) * CELL);
    return core.length ? [...this.net.bucket(cx, cy), ...core] : this.net.bucket(cx, cy);
  }

  /** (track 2) the road an edge is, its axis and its key — what the lot planner hangs rows on */
  roadOf(e: PathEdge): EdgeInfo { return this.infoFor(e); }

  /**
   * (owner's night reports, Sep 10: "streetlight on the road", "trees really close to the road", "bushes on the road")
   * The picture is the *stroke* (§40): a band `total` wide measured square to the curve. The tiles are the raster: a
   * band `total` wide measured along the axis, which on a diagonal is narrower than the stroke by cos θ — so a tile
   * the raster calls pavement, or calls nothing at all, can sit under the painted carriageway. This is the stroke's
   * answer for a point: its distance square to the nearest road's centre line, with that road's widths, or undefined
   * when no road's stroke comes within `slack` of it. A few array reads per nearby edge; the generator asks it for
   * every tile it would put a tree or a lamp on. `x, y` name a tile; the stroke runs on the tile centres, so the
   * distance is between tile columns and the curve's column in the tile's row.
   */
  strokeOff(x: number, y: number, slack = 1): { off: number; width: number; total: number } | undefined {
    let best: { off: number; width: number; total: number } | undefined;
    for (const list of [this.net.near(x, y), this.coreNear(x, y)]) for (const e of list) {
      if (e.cls === 'rail' || x < e.x0 - BOX_PAD || x > e.x1 + BOX_PAD || y < e.y0 - BOX_PAD || y > e.y1 + BOX_PAD) continue;
      const i = this.infoFor(e), vertical = i.axis === 'v';
      const u = vertical ? y : x, t = vertical ? x : y;
      const j = Math.floor(u) - i.u0; if (j < 0 || j >= i.centres.length) continue; // (Sep 23: the tile the point is in — a point off the tile grid read centres[1.5], undefined, and every offset came out NaN)
      const c = i.centres[j]; if (Number.isNaN(c)) continue;
      const reach = i.total / 2 + slack;
      if (Math.abs(t - c) > reach * 1.5) continue;
      // the curve's slope across the rows, one-sided at the ends (the table's outer rows are NaN: a flat guess there
      // over-reported a diagonal road's offset by up to 41 %, and the generator planted trees on it)
      const a = j > 0 ? i.centres[j - 1] : Number.NaN, b = j + 1 < i.centres.length ? i.centres[j + 1] : Number.NaN;
      const dc = Number.isNaN(a) ? (Number.isNaN(b) ? 0 : b - c) : Number.isNaN(b) ? c - a : (b - a) / 2;
      const off = Math.abs(t - c) / Math.sqrt(1 + dc * dc);
      if (off > reach) continue;
      if (!best || off < best.off) best = { off, width: i.road.width, total: i.total };
    }
    return best;
  }

  // ───────────────────────────── §42: the lanes — Phase A's contract, what a curve-following driver reads ─────────────
  /** the per-edge lane geometry: the curve on the tile centres, its cumulative arc length, the class's lane offset */
  private laneInfo = new Map<PathEdge, { pts: [number, number][]; len: number[]; total: number; off: number }>();
  private laneOf(e: PathEdge) {
    let l = this.laneInfo.get(e);
    if (l) return l;
    const info = this.infoFor(e), w = info.road.width;
    const even = (w & 1) === 0, ox = 0.5 + (even && info.axis === 'v' ? 0.5 : 0), oy = 0.5 + (even && info.axis === 'h' ? 0.5 : 0);
    const pts: [number, number][] = [];
    for (let i = 0; i < e.pts.length; i += 2) pts.push([e.pts[i] + ox, e.pts[i + 1] + oy]);
    const len = [0]; for (let i = 1; i < pts.length; i++) len.push(len[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    // the lane offsets `ai.ts`'s laneCentre uses: two 2.5 m lanes either side of the centre on a two-lane road, one lane
    // on a narrow one but never so far out that a 2 m car overlaps the kerb
    const off = w >= 8 ? 1.7 : w >= 5 ? 1.35 : Math.max(0, Math.min(w / 4, w / 2 - 1 - 0.15));
    l = { pts, len, total: len[len.length - 1], off };
    if (this.laneInfo.size > 20000) this.laneInfo.clear();
    this.laneInfo.set(e, l);
    return l;
  }
  private static seg(len: number[], d: number): number { let i = 1; while (i < len.length - 1 && len[i] < d) i++; return i; }
  /** point and unit tangent (a→b) at arc length d, clamped to the edge */
  private static along(l: { pts: [number, number][]; len: number[]; total: number }, d: number): { x: number; y: number; tx: number; ty: number } {
    d = Math.max(0, Math.min(l.total, d));
    const i = PathRoads.seg(l.len, d), a = l.pts[i - 1], b = l.pts[i], span = l.len[i] - l.len[i - 1] || 1, k = (d - l.len[i - 1]) / span;
    const n = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
    return { x: a[0] + (b[0] - a[0]) * k, y: a[1] + (b[1] - a[1]) * k, tx: (b[0] - a[0]) / n, ty: (b[1] - a[1]) / n };
  }

  /** The edge's length along its curve, in tiles. */
  laneLength(e: PathEdge): number { return this.laneOf(e).total; }

  /**
   * The nearest road edge to a point (never a railway): the arc length `s` along it from `a`, the signed distance `d`
   * from its centre line (negative = left of travel a→b), and `side` = the sign of `d`. `nearest()` with the arc length.
   */
  locate(x: number, y: number, max = 24): { e: PathEdge; s: number; d: number; side: 1 | -1 } | undefined {
    let best: { e: PathEdge; s: number; d: number; side: 1 | -1 } | undefined, bestD = max;
    // the network's edges first; a core street is only a candidate when the point is not already on a network road's
    // carriageway — at a T the avenue's lane wins over the cross street ending in it
    const lists = [this.net.near(x, y), this.coreNear(x, y)];
    for (let li = 0; li < lists.length; li++) for (const e of lists[li]) {
      if (li === 1 && best && Math.abs(best.d) <= this.infoFor(best.e).road.width / 2 + 0.5) break;
      if (e.cls === 'rail' || x < e.x0 - max || x > e.x1 + max || y < e.y0 - max || y > e.y1 + max) continue;
      const l = this.laneOf(e);
      for (let i = 1; i < l.pts.length; i++) {
        const [ax, ay] = l.pts[i - 1], [bx, by] = l.pts[i], vx = bx - ax, vy = by - ay, len2 = vx * vx + vy * vy || 1;
        let t = ((x - ax) * vx + (y - ay) * vy) / len2; t = t < 0 ? 0 : t > 1 ? 1 : t;
        const px = ax + vx * t, py = ay + vy * t, d = Math.hypot(x - px, y - py);
        if (d >= bestD) continue;
        bestD = d;
        const cross = vx * (y - py) - vy * (x - px); // > 0: right of travel a→b (y down)
        best = { e, s: l.len[i - 1] + (l.len[i] - l.len[i - 1]) * t, d: cross >= 0 ? d : -d, side: cross >= 0 ? 1 : -1 };
      }
    }
    return best;
  }

  /**
   * The point on the driving lane of `e` for direction `dir` (+1 = a→b, −1 = b→a) at arc length `s` measured in the
   * direction of travel — `s = 0` is the node the car came from either way. Right-hand traffic by the class's lane
   * offset; `overtaking` is the mirror lane. Position on the tile centres (the renderer's shift), unit tangent in the
   * direction of travel, deck height there (0 on the ground). `s` past the end is clamped: ask `next()` before that.
   */
  laneAt(e: PathEdge, dir: 1 | -1, s: number, overtaking = false): LanePoint {
    const l = this.laneOf(e);
    const p = PathRoads.along(l, dir === 1 ? s : l.total - s);
    const tx = p.tx * dir, ty = p.ty * dir; // travel tangent
    const off = overtaking ? -l.off : l.off;
    const x = p.x - ty * off, y = p.y + tx * off; // the right-hand side of travel, y down
    let h = 0;
    const info = this.infoFor(e);
    if (info.deck) { const u = Math.floor(info.axis === 'v' ? y : x) - info.u0; if (u >= 0 && u < info.deck.length) h = info.deck[u]; }
    return { x, y, tx, ty, h };
  }

  /**
   * The ways on from the node a car on `e` driving `dir` is heading for: the junction's other arms, never a railway,
   * each with the direction to drive it and the turn it is — by the angle between the tangent arriving and the tangent
   * leaving (right-hand turns clockwise on screen). Empty at a dead end.
   */
  next(e: PathEdge, dir: 1 | -1): { edge: PathEdge; dir: 1 | -1; turn: 'left' | 'right' | 'straight' | 'back' }[] {
    const node = dir === 1 ? e.b : e.a;
    const j = this.net.junctionAt(node.cx, node.cy);
    if (!j) return [];
    const l = this.laneOf(e), here = PathRoads.along(l, dir === 1 ? l.total : 0);
    const ax = here.tx * dir, ay = here.ty * dir;
    const out: { edge: PathEdge; dir: 1 | -1; turn: 'left' | 'right' | 'straight' | 'back' }[] = [];
    for (const arm of j.arms) {
      if (arm.edge === e || arm.edge.cls === 'rail') continue;
      const d2: 1 | -1 = arm.away, l2 = this.laneOf(arm.edge), leave = PathRoads.along(l2, d2 === 1 ? 0 : l2.total);
      const bx = leave.tx * d2, by = leave.ty * d2;
      const dot = ax * bx + ay * by, cross = ax * by - ay * bx;
      const turn = dot > Math.cos(Math.PI / 6) ? 'straight' : dot < -Math.cos(Math.PI / 6) ? 'back' : cross > 0 ? 'right' : 'left';
      out.push({ edge: arm.edge, dir: d2, turn });
    }
    return out;
  }

  /**
   * The next thing along the lane to slow for, past arc length `s` in the direction of travel, with its arc length: the
   * junction ahead (at the box edge, where the crosswalk is — `at` is where to stop), a level crossing with a railway,
   * or a shore where the road's tiles end. Undefined when the edge ends in a bend or a pass-through with nothing to
   * slow for. A driver asks once per edge and keeps the answer.
   */
  crossingAt(e: PathEdge, dir: 1 | -1, s: number): { kind: 'junction' | 'rail' | 'shore'; at: number } | undefined {
    const l = this.laneOf(e), total = l.total;
    const found: { kind: 'junction' | 'rail' | 'shore'; at: number }[] = [];
    // a railway at grade across this road: a level crossing at its parameter
    for (const o of this.net.near((e.a.x + e.b.x) / 2, (e.a.y + e.b.y) / 2)) {
      if (o.cls !== 'rail' || o.x1 < e.x0 || o.x0 > e.x1 || o.y1 < e.y0 || o.y0 > e.y1) continue;
      const t = PathNet.crossing(e, o); if (t === undefined) continue;
      if (this.net.oversOf(o).some((ov) => ov.under === e)) continue; // the railway goes over on a viaduct: nothing to stop for
      const at = dir === 1 ? t * total : total - t * total;
      found.push({ kind: 'rail', at: at - 3 });
    }
    // the shore: the first tile along the lane with no road under it (a wet run past the class's longest deck)
    const scratch: RoadHit[] = [];
    for (let d = Math.ceil(s); d < total; d += 1) {
      const p = this.laneAt(e, dir, d);
      if (!this.hitsAt(Math.floor(p.x), Math.floor(p.y), scratch).length) { found.push({ kind: 'shore', at: d - 2 }); break; }
    }
    // the junction ahead: stop at the box's edge, the widest crossing arm's half-width short of the node
    const node = dir === 1 ? e.b : e.a, j = this.net.junctionAt(node.cx, node.cy);
    if (j && j.arms.length >= 3) { let w = 0; for (const a of j.arms) if (a.edge !== e && a.edge.cls !== 'rail') w = Math.max(w, this.infoFor(a.edge).total); found.push({ kind: 'junction', at: total - w / 2 - 1 }); }
    let best: { kind: 'junction' | 'rail' | 'shore'; at: number } | undefined;
    for (const f of found) if (f.at >= s && (!best || f.at < best.at)) best = f;
    return best;
  }

  /**
   * (track 1, §40) The roads that touch a box of tiles, coarsest first, for the renderer to *stroke* along their curves:
   * the tile raster is the collision model and stays, but a kerb or a centre line drawn one axis-aligned tile at a time
   * reads as a staircase of little lines on a bend (owner, Sep 10: "the sides and center line are more like lines and
   * not separate small lines going the wrong way"). `dead` says which end stops in the open (a node with no other road),
   * where the stroke is cut short so its round cap does not reach past the tiles. Railways are not here: the track is
   * drawn per tile and does not bend enough to show it.
   */
  strokesIn(x0: number, y0: number, x1: number, y1: number, rails = false): { e: PathEdge; info: EdgeInfo; dead: [boolean, boolean]; /** the widest road meeting at each end, total tiles across — where a centre line stops for a junction, 0 at a bend or a dead end */ box: [number, number] }[] {
    const seen = new Set<string>(), out: { e: PathEdge; info: EdgeInfo; dead: [boolean, boolean]; box: [number, number] }[] = [];
    const endOf = (cx: number, cy: number, self: PathEdge): [boolean, number] => {
      const j = this.net.junctionAt(cx, cy);
      if (!j || j.arms.length <= 1) return [true, 0];
      if (j.arms.length === 2) return [false, 0];
      let w = 0; for (const a of j.arms) if (a.edge !== self) w = Math.max(w, this.infoFor(a.edge).total);
      return [false, w];
    };
    for (let cy = Math.floor(y0 / CELL); cy <= Math.floor((y1 - 1) / CELL); cy++) for (let cx = Math.floor(x0 / CELL); cx <= Math.floor((x1 - 1) / CELL); cx++) {
      for (const e of this.net.bucket(cx, cy)) {
        if ((e.cls === 'rail' && !rails) || seen.has(e.key) || e.x1 < x0 || e.x0 > x1 || e.y1 < y0 || e.y0 > y1) continue;
        seen.add(e.key);
        const [da, ba] = e.cls === 'rail' ? [false, 0] : endOf(e.a.cx, e.a.cy, e), [db, bb] = e.cls === 'rail' ? [false, 0] : endOf(e.b.cx, e.b.cy, e); // (a railway is drawn by the tiles on the ground; its viaducts are asked for by the deck layer, `rails`)
        out.push({ e, info: this.infoFor(e), dead: [da, db], box: [ba, bb] });
      }
      // (§56) the core's cross streets: both ends join an avenue, so neither is dead and each is cut back into its avenue
      // (via coreNear: a street keyed to a cell west of this window still crosses it)
      for (const e of this.coreNear((cx + 0.5) * CELL, (cy + 0.5) * CELL)) {
        if (seen.has(e.key) || e.x1 < x0 || e.x0 > x1 || e.y1 < y0 || e.y0 > y1) continue;
        seen.add(e.key);
        out.push({ e, info: this.infoFor(e), dead: [false, false], box: this.coreBox.get(e) ?? [0, 0] });
      }
    }
    out.sort((p, q) => p.e.level - q.e.level);
    return out;
  }
  /** Everything constant along one edge, computed once. */
  private infoFor(e: PathEdge): EdgeInfo {
    let i = this.info.get(e);
    if (i) return i;
    const road = this.roadFor(e);
    // The axis is the edge's **chord**, not its local tangent. A curve can lean far enough that one end runs closer to
    // the other axis than the chord does, and taking the tangent would then report two different axes for one road —
    // which would make `ai.ts` swap `ai.axis` mid-block and drive the car into the kerb. One road, one axis.
    const axis: Axis = Math.abs(e.b.x - e.a.x) >= Math.abs(e.b.y - e.a.y) ? 'h' : 'v';
    const vertical = axis === 'v';
    const u0 = Math.floor(vertical ? e.y0 : e.x0), u1 = Math.ceil(vertical ? e.y1 : e.x1);
    const centres = new Float32Array(Math.max(1, u1 - u0 + 1));
    const t0 = vertical ? e.a.x : e.a.y;
    for (let u = u0; u <= u1; u++) { const c = crossing(e, vertical, u, t0); centres[u - u0] = c === undefined ? Number.NaN : c; }
    i = { road, total: road.width + 2 * (road.sidewalk ?? 0), axis, k: edgeKey(e), u0, centres };
    this.net.oversOf(e); // (track 1) the grade separations must be resolved before the deck rows are read from them
    const deck = deckRows(e, vertical, u0, centres, this.net);
    if (deck) i.deck = deck;
    this.info.set(e, i);
    return i;
  }

  /**
   * All roads covering tile (x, y), coarsest level first — `RoadNet.hitsAt`'s contract, answered from the paths.
   * `out` is cleared and refilled, so a caller with a scratch array keeps it.
   *
   * The `RoadHit` objects themselves are fresh, deliberately, even though pooling them would be faster: `ai.ts` holds
   * a hit from one call across the *next* call on the same scratch array (`hit` survives while `ph` is fetched), and a
   * pooled object would be rewritten under it. Clearing the array is part of the contract; recycling its contents is
   * not. Nothing is allocated when a tile has no road, which is nearly every tile.
   */
  /** one edge's hit on a tile, if the tile is under its painted width (the body of `hitsAt` for a plain edge) */
  private edgeHit(e: PathEdge, x: number, y: number, out: RoadHit[]): void {
    if (x < e.x0 - BOX_PAD || x > e.x1 + BOX_PAD || y < e.y0 - BOX_PAD || y > e.y1 + BOX_PAD) return;
    const i = this.infoFor(e);
    const vertical = i.axis === 'v';
    // `u` runs along the road, `t` across it — the same split `RoadNet.hitsAlong` makes, and the reason `off` and
    // `pos` mean here what they mean there.
    const u = vertical ? y : x, t = vertical ? x : y;
    const centre = this.centreAt(i, u);
    if (centre === undefined) return; // the row is past this edge's ends: the road genuinely stops at the node
    // Rounded, because `pos` is a *tile* column in every caller that reads it: `off === 0` has to be reachable
    // exactly (render-static.ts and the platform finder in generator.ts both test it), and it only is if the centre
    // sits on a tile.
    const pos = Math.round(centre);
    const d = i.deck ? i.deck[u - i.u0] : 0;
    if (!RoadNet.covers(pos, i.total, t)) {
      // a deck's rows are rasterised on the rounded centre while the deck is painted on the exact one, so along a curve
      // the tiled edge steps a column in and out of the paint; a car in the outer lane found the railing on the water tile
      // beside the step and crashed into it (owner, Sep 11: "stuck driving into the bridge"). A tile the paint reaches is
      // deck too — a ledge: a floor, no railing, no pillar.
      if (!(d > 0 && Math.abs(t - centre) <= i.total / 2 + 0.5)) return;
      out.push({ road: i.road, axis: i.axis, level: e.level, k: i.k, pos, off: t - pos, deck: d, ledge: true, centre });
      return;
    }
    const hit: RoadHit = { road: i.road, axis: i.axis, level: e.level, k: i.k, pos, off: t - pos, centre };
    if (d > 0) hit.deck = d;
    out.push(hit);
  }
  hitsAt(x: number, y: number, out: RoadHit[] = []): RoadHit[] {
    out.length = 0;
    // (track 1) inside a junction box the box answers, not the arms: the owner's "intersection piece" (§39)
    const box = this.boxAt(x, y);
    if (box) {
      // each axis's road runs straight through the box — on its own arm's centre line where an arm covers this row
      // (so the centre never jumps under a car entering the box), on the node's where none does (the far side of a T)
      let deck = 0;
      for (const a of box.arms) { const d = a.info.deck; if (d) { const u = (a.info.axis === 'v' ? y : x) - a.info.u0; if (u >= 0 && u < d.length && d[u] > deck) deck = d[u]; } }
      for (const axis of ['h', 'v'] as const) {
        const side = axis === 'h' ? box.h : box.v;
        if (!side) continue;
        const u = axis === 'v' ? y : x, t = axis === 'v' ? x : y;
        let pos = axis === 'v' ? box.nx : box.ny;
        for (const a of box.arms) { if (a.info.axis !== axis) continue; const c = this.centreAt(a.info, u); if (c !== undefined) { pos = Math.round(c); break; } }
        // the hit contract: a hit covers its tile — at the box's corner an arm's own centre may not, and the node's does
        if (!RoadNet.covers(pos, side.total, t)) pos = axis === 'v' ? box.nx : box.ny;
        const hit: RoadHit = { road: side.road, axis, level: side.level, k: side.k, pos, off: t - pos };
        if (deck > 0) hit.deck = deck;
        out.push(hit);
      }
      // a railway never joins a box, but it crosses one (a viaduct over the junction, or a level crossing beside it):
      // its hit still belongs to the tile, or the deck has a hole where the box is (owner, Sep 11: "I can't cross the
      // line in the middle" — the walker stepped off the one deck column the box had left)
      for (const e of this.net.near(x, y)) if (e.cls === 'rail') this.edgeHit(e, x, y, out);
      builtHere(this.net, this.reg, x, y, out);
      if (out.length > 1) out.sort(byLevel);
      return out;
    }
    for (const e of this.net.near(x, y)) this.edgeHit(e, x, y, out);
    for (const e of this.coreNear(x, y)) this.edgeHit(e, x, y, out); // (§56) the core's cross streets
    builtHere(this.net, this.reg, x, y, out);
    // Coarsest first, exactly as `RoadNet` sorts: callers take `hits[0]` as "the road this tile mainly belongs to"
    // and `find` the first match of a class, and both readings depend on the order.
    if (out.length > 1) out.sort(byLevel);
    return out;
  }

  /**
   * (track 1, the owner's request after the first drive on the path world — MAPGEN_DECISION.md §39) **The junction
   * box.** Two curved roads meeting at a node each stop at the node's row, so the wider one left a notch of grass in
   * the corner and the narrower one's crosswalk reached only to the middle of the road. The owner named the hack
   * (extend the wider road across the narrower) and the real thing: *"an intersection piece of road to which the two
   * or more roads connect"*. This is that piece, and it costs nothing new because the junction is already an object:
   * a rectangle on the node as wide as the widest arm of each axis, inside which the tile belongs to both roads at
   * once, straight through — exactly what a lattice crossing was, so the generator's carriageway/sidewalk rule and the
   * renderer's crosswalks on the box's outer rows work unchanged. A bend (arms of one axis) gets a square patch, which
   * is the corner fill; an interchange (`Junction.over`) keeps its arms' own decks and gets no box.
   */
  private boxAt(x: number, y: number): JunctionBox | undefined {
    const cx = Math.floor(x / CELL), cy = Math.floor(y / CELL);
    // a node may sit up to about a cell outside its own cell (jitter + flow), and a box reaches a few tiles further
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const b = this.boxFor(cx + dx, cy + dy);
      if (b && x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1) return b;
    }
    return undefined;
  }

  /** (Sep 12, the lights) every junction box whose node lies in the window — what the renderer hangs traffic lights on */
  private jScratch: Junction[] = [];
  junctionBoxes(x0: number, y0: number, x1: number, y1: number, out: JunctionBox[] = []): JunctionBox[] {
    out.length = 0;
    for (const j of this.net.junctionsIn(x0, y0, x1, y1, this.jScratch)) { const b = this.boxFor(j.cx, j.cy); if (b) out.push(b); }
    return out;
  }
  private boxFor(cx: number, cy: number): JunctionBox | null {
    const key = (cx & 0x3fffff) * 0x400000 + (cy & 0x3fffff);
    const had = this.boxes.get(key);
    if (had !== undefined) return had;
    if (this.boxes.size > 20000) this.boxes.clear();
    const j = this.net.junctionAt(cx, cy);
    const box = j ? this.makeBox(j) : null;
    this.boxes.set(key, box);
    return box;
  }

  private makeBox(j: Junction): JunctionBox | null {
    if (j.over) return null; // grade-separated: the trunk's deck and the avenue beneath are the arms' own business
    let h: JunctionBox['h'], v: JunctionBox['v'];
    const arms: JunctionBox['arms'] = [];
    for (const a of j.arms) {
      const i = this.infoFor(a.edge);
      arms.push({ info: i });
      const side = { road: i.road, level: a.edge.level, k: i.k, total: i.total };
      if (i.axis === 'h') { if (!h || i.total > h.total) h = side; } else if (!v || i.total > v.total) v = side;
    }
    if (!h && !v) return null;
    // the extents: across a road of one axis, the widest arm of the *other* axis decides how far the box reaches
    // along it; a bend has arms of one axis only, and its patch is as long as it is wide
    const wAlongX = v?.total ?? h!.total, wAlongY = h?.total ?? v!.total;
    const nx = Math.round(j.x), ny = Math.round(j.y);
    return {
      nx, ny, h, v, arms,
      x0: nx - Math.floor((wAlongX - 1) / 2), x1: nx + Math.floor(wAlongX / 2),
      y0: ny - Math.floor((wAlongY - 1) / 2), y1: ny + Math.floor(wAlongY / 2),
    };
  }
}

/** (track 1) a junction as the generator sees it: a rectangle of tiles that belong to both roads at once, straight through */
export interface JunctionBox {
  nx: number; ny: number;
  x0: number; y0: number; x1: number; y1: number;
  /** the widest arm of each axis, or none (a bend, a dead end) */
  h?: { road: Road; level: number; k: number; total: number };
  v?: { road: Road; level: number; k: number; total: number };
  /** every arm, for the centre line and the deck under a box tile */
  arms: { info: EdgeInfo }[];
}

/** rows between coarse samples when looking for water under a road: a pond narrower than this is missed on purpose */
const WET_STEP = 4;
/** the shortest ramp a deck may climb over: OVER_H at `RAMP_GRADE`, with a little in hand */
export const RAMP_MIN = Math.ceil(OVER_H / (RAMP_GRADE - 0.03));

/**
 * (track 2, the deck seam) The deck profile of an edge, per row: undefined when the road stays on the ground. Two things
 * raise a road. **Water under the centre line** makes a bridge — every run of wet rows, found coarsely (`WET_STEP`) and
 * then walked to its exact ends — and it needs nothing from the network: an edge that crosses water gets its bridge the
 * moment the adapter sees it. **An `Over`** resolved by the network raises a crossing. Both take the lattice's shape,
 * flat for `OVER_FLAT` beyond the raised stretch and down over `OVER_RAMP` rows either side, so the cars, the walkers,
 * the renderer's pillars and parapets and the see-through-when-under rule all keep working unchanged. Where two spans
 * meet, the higher wins. A ramp is clipped at the edge's end row: a bridge that starts at a node has no run-up there.
 */
export function deckRows(e: PathEdge, vertical: boolean, u0: number, centres: Float32Array, net: PathNet): Float32Array | undefined {
  const fields = net.fields, n = centres.length, sea = fields.seaLevel;
  const spans: { lo: number; hi: number; h: number; /** the deck runs flat through this end's node: the next edge carries it on (a railway viaduct through its own node) */ throughLo?: boolean; throughHi?: boolean }[] = [];
  const wet = (j: number): boolean => { const c = centres[j]; if (Number.isNaN(c)) return false; const u = u0 + j; return fields.elevation(vertical ? c : u, vertical ? u : c) < sea; };
  for (let j = 0; j < n; ) {
    if (!wet(j)) { j += WET_STEP; continue; }
    let lo = j, hi = j;
    while (lo > 0 && wet(lo - 1)) lo--;
    while (hi + 1 < n && wet(hi + 1)) hi++;
    spans.push({ lo, hi, h: OVER_H });
    j = hi + 1;
  }
  // (§43, Phase B) a ramp must be down before the node: the rows of this edge end at its nodes, and a ramp cut off there
  // was a cliff — and at a junction the other arms meet the road at grade, so the ramp has to be down before the box.
  // A ramp is `OVER_RAMP` rows where there is room and never shorter than `RAMP_MIN`: a vehicle probes the tiles under
  // its corners at the height of its centre plus `RAMP_GRADE` times their reach (physics.ts), so a ramp steeper than that
  // is a wall at its foot (owner, Sep 10 night: "can't enter the bridge from the left here" — a six-row ramp). Where a
  // span would need its ramp closer to the node than that, the span is cut short and the water at the node end is
  // crossed at grade instead.
  const rowA = Math.round(vertical ? e.a.y : e.a.x) - u0, rowB = Math.round(vertical ? e.b.y : e.b.x) - u0;
  // (Sep 29, a bug report: "the bridge stops weird and does not connect to the other road here", seed 1001 at -162,-783) a bend is
  // a box too: the next road leaves the node at an angle, its first rows lie across this edge's last ones, and a ramp that came down
  // on the node itself stood over them — the deck ended in a cut above the road that turned away under it. Past 40° of turn the
  // ramp is down the other road's half-width (and one) before the node, as at a junction.
  const bendHalf = (j: NonNullable<ReturnType<PathNet['junctionAt']>>, node: PathNode): number => {
    const mine = j.arms.find((a) => a.edge === e), other = j.arms.find((a) => a.edge !== e);
    if (!mine || !other || other.edge.cls === 'rail') return 0;
    const dir = (a: typeof mine): [number, number] => { const [x, y] = edgeAt(a.edge, a.away === 1 ? 0.15 : 0.85); return [x - node.x, y - node.y]; };
    const [ax, ay] = dir(mine), [bx, by] = dir(other), cos = (ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by) || 1);
    return cos > -Math.cos((40 * Math.PI) / 180) ? HALF_WIDTH[other.edge.cls] + 1 : 0; // (straight on is cos −1)
  };
  const boxHalf = (node: PathNode): number => { const j = net.junctionAt(node.cx, node.cy); if (j?.arms.length === 2) return bendHalf(j, node); if (!j || j.arms.length < 3) return 0; let w = 0; for (const a of j.arms) if (a.edge !== e && a.edge.cls !== 'rail') w = Math.max(w, HALF_WIDTH[a.edge.cls] + 1); return w; };
  const lowRow = Math.min(rowA, rowB) + boxHalf(rowA < rowB ? e.a : e.b), highRow = Math.max(rowA, rowB) - boxHalf(rowA < rowB ? e.b : e.a);
  // an interchange end keeps its flat deck over the node (the trunk is carried over it by design); any other end must
  // be reached on the ground, so a span that runs too close to it is cut short — a few rows of road at the water's
  // edge rather than a deck the side arms cannot join
  const overEnd = (node: PathNode): boolean => { const j = net.junctionAt(node.cx, node.cy); return !!j?.over && j.over === PathNet.axisOf(e); };
  const minRun = OVER_FLAT + RAMP_MIN;
  const lowEnd = rowA < rowB ? e.a : e.b, highEnd = rowA < rowB ? e.b : e.a;
  for (const s of spans) {
    if (!overEnd(lowEnd)) s.lo = Math.max(s.lo, lowRow + minRun);
    if (!overEnd(highEnd)) s.hi = Math.min(s.hi, highRow - minRun);
  }
  let kept = spans.filter((s) => s.hi >= s.lo);
  // (track 1) grade separations — `PathEdge.overs`, resolved by the network where this edge is carried over another
  // (a railway over a street, a trunk over a town's avenue at an interchange): a flat deck between t0 and t1
  for (const o of net.oversOf(e)) { // (resolved now if the network has not been past this edge yet: the adapter must not depend on who asked first)
    const at = (t: number): number => { const [px, py] = edgeAt(e, t); return Math.round(vertical ? py : px) - u0; }; // (the edge's own curve: a smoothed road's is a cubic)
    const ua = at(o.t0), ub = at(o.t1);
    // an over reaching a node end (t0 = 0 or t1 = 1: the network carried it through the node from the next edge) stays flat
    // to the edge's last row, so the two edges' decks meet at height instead of each ramping off its own end
    const throughA = o.t0 === 0, throughB = o.t1 === 1, low = ua <= ub;
    kept.push({ lo: Math.min(ua, ub), hi: Math.max(ua, ub), h: OVER_H, throughLo: low ? throughA : throughB, throughHi: low ? throughB : throughA });
  }
  // (§43, Phase B) a ramp must not land on a road: every other road this edge crosses within a ramp's reach of a span
  // pulls the flat part out over it (its half-width and the clearance), so what crosses under a bridge is always under
  // the flat deck with headroom, never under the embankment (owner, Sep 10: "car can't drive under the bridge")
  if (kept.length) {
    const at = (t: number): number => { const [px, py] = edgeAt(e, t); return Math.round(vertical ? py : px) - u0; }; // (the edge's own curve: a smoothed road's is a cubic)
    const crossings: { row: number; half: number }[] = [];
    const seen = new Set<PathEdge>();
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) for (const o of net.cellEdges(e.a.cx + dx, e.a.cy + dy)) {
      if (o === e || seen.has(o) || o.cls === 'rail') continue;
      seen.add(o);
      if (o.x1 < e.x0 || o.x0 > e.x1 || o.y1 < e.y0 || o.y0 > e.y1) continue;
      if (o.a === e.a || o.a === e.b || o.b === e.a || o.b === e.b) continue; // meets at a node: a junction, not a crossing
      const t = PathNet.crossing(e, o); if (t === undefined) continue;
      crossings.push({ row: at(t), half: HALF_WIDTH[o.cls] + OVER_FLAT });
    }
    // the flat part reaches out over a crossing within a ramp's reach; where that would leave no room for the ramp
    // before the node, the deck starts beyond the crossing instead (a level crossing at the node end, the bridge after).
    // A span carried through a node (a railway viaduct the next edge continues) has no ramp at that end to make room for
    if (crossings.length) for (let pass = 0; pass < 3; pass++) {
      let grew = false;
      for (const s of kept) for (const c of crossings) {
        const loFloor = s.throughLo || overEnd(lowEnd) ? -Infinity : lowRow + minRun, hiCeil = s.throughHi || overEnd(highEnd) ? Infinity : highRow - minRun;
        const reach = OVER_FLAT + OVER_RAMP;
        if (c.row + c.half >= s.lo - reach && c.row - c.half <= s.hi + reach) {
          let lo = Math.min(s.lo, c.row - c.half), hi = Math.max(s.hi, c.row + c.half);
          if (lo < loFloor) lo = Math.max(s.lo, c.row + c.half + reach + 1);
          if (hi > hiCeil) hi = Math.min(s.hi, c.row - c.half - reach - 1);
          lo = Math.floor(lo); hi = Math.ceil(hi); // (rows are integers: a half-width can be fractional, and a fractional row is not an index at all)
          if (lo !== s.lo || hi !== s.hi) { s.lo = Math.max(0, lo); s.hi = Math.min(n - 1, hi); grew = true; }
        }
      }
      if (!grew) break;
    }
  }
  kept = kept.filter((s) => s.hi >= s.lo).sort((p, q) => p.lo - q.lo);
  if (!kept.length) return undefined;
  // two spans whose ramps would meet in the air are one viaduct: a dip between them was a hollow the road fell into
  for (let i = 1; i < kept.length; ) { const p = kept[i - 1], q = kept[i]; if (q.lo - p.hi < 2 * (OVER_FLAT + OVER_RAMP)) { p.hi = Math.max(p.hi, q.hi); p.h = Math.max(p.h, q.h); p.throughHi = p.throughHi || q.throughHi; p.throughLo = p.throughLo || q.throughLo; kept.splice(i, 1); } else i++; }
  const out = new Float32Array(n);
  for (const s of kept) {
    const flat0 = s.throughLo ? 0 : s.lo - OVER_FLAT, flat1 = s.throughHi ? n - 1 : s.hi + OVER_FLAT;
    // the gentle ramp wherever there is room for it, the shortest a car climbs where there is not
    const rampLo = s.throughLo ? 0 : Math.max(RAMP_MIN, Math.min(OVER_RAMP, flat0 - lowRow)), rampHi = s.throughHi ? 0 : Math.max(RAMP_MIN, Math.min(OVER_RAMP, highRow - flat1));
    for (let j = Math.max(0, Math.floor(flat0 - rampLo)); j <= Math.min(n - 1, Math.ceil(flat1 + rampHi)); j++) {
      const dist = j < flat0 ? flat0 - j : j > flat1 ? j - flat1 : 0, ramp = j < flat0 ? rampLo : rampHi;
      const h = dist === 0 ? s.h : Math.round(s.h * (1 - dist / ramp) * 100) / 100;
      if (h > out[j]) out[j] = h;
    }
  }
  return out;
}

/**
 * `RoadHit.k` identifies a *line* on the lattice, and the only thing any caller outside `roads.ts` does with it is
 * hash it: `ai.ts`'s `oneWayDir` hashes `(level, k, axis)` to give a narrow road a fixed one-way direction. What that
 * needs is a number that is the same everywhere along one road and different between roads — the lattice index
 * happens to be one, an edge's identity is another. So `k` here is a hash of the edge's owning cell, direction and
 * level: stable along the edge, stable across chunks, and stable across runs.
 *
 * A caller that instead did arithmetic on `k` (multiplied it by a spacing to get a coordinate, say) would get
 * nonsense. None does today; `roads.ts` keeps that arithmetic to itself.
 */
export function edgeKey(e: PathEdge): number {
  // a core street (§56) shares its cell, direction and level with its neighbours in the cell and with the network's own
  // east edge, so its row index goes in too — or an avenue's driver would take two cross streets for one crossing
  const row = e.key.startsWith('core:') ? Number(e.key.slice(e.key.lastIndexOf(',') + 1)) : 0;
  return row ? hash(0x9e37, e.a.cx, e.a.cy, e.b.cx > e.a.cx ? 0 : 1, e.level, row) : hash(0x9e37, e.a.cx, e.a.cy, e.b.cx > e.a.cx ? 0 : 1, e.level);
}

/**
 * Where the edge's centre line crosses the scanline at `u`, or undefined if it does not reach it. When more than one
 * crossing exists — a curve that doubles back, which the bend limits make rare but not impossible — the one nearest
 * `t` wins, because that is the piece of road the tile is actually on.
 */
export function crossing(e: PathEdge, vertical: boolean, u: number, t: number): number | undefined {
  const p = e.pts, n = p.length / 2 - 1;
  const ia = vertical ? 1 : 0, ic = vertical ? 0 : 1; // along, across
  let best: number | undefined, bestD = Infinity;
  for (let i = 0; i < n; i++) {
    const a0 = p[i * 2 + ia], b0 = p[(i + 1) * 2 + ia];
    const a1 = p[i * 2 + ic], b1 = p[(i + 1) * 2 + ic];
    const lo = a0 < b0 ? a0 : b0, hi = a0 < b0 ? b0 : a0;
    if (u < lo || u > hi) continue;
    let c: number;
    if (b0 === a0) {
      // The segment lies *along* the scanline, so the whole of it is the crossing: the nearest point of it to `t` is
      // the honest answer, and it keeps a road that turns through the axis from developing a gap at the turn.
      const c0 = a1 < b1 ? a1 : b1, c1 = a1 < b1 ? b1 : a1;
      c = t < c0 ? c0 : t > c1 ? c1 : t;
    } else c = a1 + (b1 - a1) * ((u - a0) / (b0 - a0));
    const d = c > t ? c - t : t - c;
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

/**
 * Roads the *player* built (`overlay.ts`) are not part of any generated network, lattice or path — they are finished
 * 16-tile pieces pinned to lattice coordinates. `RoadNet.hitsAt` reports them and `growth.ts` depends on that to
 * refuse to build a second road on top of the first, so the adapter reports them too, on the same terms.
 */
function builtHere(net: PathNet, reg: Registry, x: number, y: number, out: RoadHit[]): void {
  const ov = net.fields.overlay;
  if (!ov.roads.size) return;
  for (const axis of ['v', 'h'] as const) {
    const t = axis === 'v' ? x : y, u = axis === 'v' ? y : x;
    const pos = Math.round(t / PIECE) * PIECE;
    const piece = ov.builtAt(axis, pos, u);
    if (!piece) continue;
    if (out.some((h) => h.axis === axis && h.pos === pos)) continue;
    const road = reg.get('road', piece.r);
    if (road && RoadNet.covers(pos, road.width + 2 * (road.sidewalk ?? 0), t)) out.push({ road, axis, level: 3, k: pos / PIECE, pos, off: t - pos, built: true });
  }
}

/*
 * ---------------------------------------------------------------------------------------------------------------
 * Where this mapping is an approximation, and what breaks if a caller believes otherwise
 * ---------------------------------------------------------------------------------------------------------------
 *
 * `pos` is a **local** coordinate, not a line identity. On the lattice `pos` is constant along a whole road, so
 * `h.pos === other.pos` is a legitimate way to ask "the same road?" and `${axis}:${pos}` is a legitimate key for a
 * crossing. Here `pos` is where the curve crosses *this* row, so it moves. Ask identity with `(level, k, axis)`
 * instead. The two places that would notice: `generator.ts`'s `railEndNear` walks a line comparing `h.pos` (rail does
 * not exist in the path network at all, so it finds nothing either way), and `ai.ts` builds a traffic-light /
 * decision key from two `pos` values — a car will re-decide at a crossing more often than it should on a curved road,
 * which costs a little jitter, not correctness.
 *
 * `off` is measured **across the axis**, not square to the road. A road at 45° therefore covers √2 as many tiles per
 * scanline as its width, which is what makes it a solid diagonal band of the right thickness rather than a dotted
 * one — but a caller reading `off` as a true perpendicular distance ("how far from the middle am I, in metres") will
 * read up to 41 % too far on a diagonal. Only `parking.ts` and `ai.ts`'s lane maths do anything quantitative with it,
 * and both want the across-the-axis number, since both then move the car in that same axis.
 *
 * `level` is the path network's level, not the lattice's, and the two do not mean the same spacings (the lattice is
 * 128/64/32/16 tiles, the path net is 512/256/128/64 between nodes). Callers that treat level only as a ranking —
 * `mode === 'major' && h.level > 0`, the shore rule, the sort — are fine. Nothing multiplies a level by a spacing
 * outside `roads.ts`.
 *
 * There is **no rail**. `PathClass` has no rail member, so every `find(h => h.road.class === 'rail')` returns
 * undefined: level crossings, platforms, `railEndNear` and `train.ts` all go quiet. That is a gap in the path network,
 * not in the adapter, and it is the one thing that genuinely stops working rather than degrading.
 *
 * There is **no overpass**. `RoadNet.overpassH` / `overpassAt` / `frontageAt` are lattice geometry (they need two
 * straight level-0 lines crossing at a known multiple of `SEG`) and have no path-network equivalent yet; junction
 * objects are track 1's job. `generator.ts` calls `net.overpassH(h, x, y)` on every hit, so it needs the *net*, not
 * just the adapter, and this adapter deliberately does not pretend to be one.
 */
